import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/src/renderer/components/ui/Button'
import { Input } from '@/src/renderer/components/ui/Input'
import { PasswordInput } from '@/src/renderer/components/ui/PasswordInput'
import { Link } from '@/src/renderer/components/ui/Link'
import { SocialButton } from '@/src/renderer/components/ui/SocialButton'
import { SmileyIcon } from '@/src/renderer/components/icons/SmileyIcon'
import { SkipConfirmationDialog } from './SkipConfirmationDialog'
import { ForgotPasswordForm } from '../../components/ForgotPasswordForm'
import { ResetPasswordForm } from '../../components/ResetPasswordForm'
import { RedirectingToApp } from '../../components/RedirectingToApp'
import { OpenInAppPrompt } from '../../components/OpenInAppPrompt'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { useWindowContext } from '@/src/renderer/components/layout/Titlebar'
import { sendEmailOTP, verifyEmailOTP, loginUser, syncUserLanguage } from '@/src/renderer/lib/supabase/auth'
import { supabase } from '@/src/renderer/lib/supabase/client'
import { db } from '@/src/renderer/lib/db'
import { useAuth } from '@/src/renderer/contexts/AuthProvider'
import { useLanguage } from '@/src/renderer/contexts/LanguageProvider'
import { startSetupFlow, getSetupFlowProgress } from '@/src/renderer/lib/supabase/setupFlow'
import { getRouteForStep } from '@/src/renderer/lib/types/setupFlow'
import './SetupPage.scss'

/**
 * SetupPage Component
 *
 * Initial onboarding page that handles both user registration and login.
 * Supports multiple authentication methods and intelligently switches between
 * login and registration modes based on user history.
 *
 * @remarks
 * Authentication Methods:
 * - Email + Password (Login mode)
 * - Email + OTP (Registration mode)
 * - Social Login (Google/Apple) - Coming soon
 * - Skip to Offline Mode (Electron only)
 *
 * Smart Mode Detection:
 * - Shows Login by default if user has logged in before
 * - Shows Registration for first-time users
 *
 * Navigation Flow:
 * - Registration → OTP Verification → Profile Setup → Questionary → Home
 * - Login → Profile Setup (if incomplete) → Home
 * - Skip → Offline Mode → Home
 *
 * @example
 * ```tsx
 * <Route path="/" element={<SetupPage />} />
 * ```
 */
export default function SetupPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const platformAPI = getPlatformAPI()
  const windowContext = useWindowContext()
  const auth = useAuth()
  const { language } = useLanguage()
  const [showSkipConfirmation, setShowSkipConfirmation] = useState(false)
  const [showOpenInAppPrompt, setShowOpenInAppPrompt] = useState(false)
  // Greet the returning user by name on the login title. `lastFullName` is a
  // device-level hint written by updateUserProfile / SetupProfilePage that
  // intentionally survives `wipeUserLocalState` on logout. Dexie is checked
  // as a fallback in case localStorage was cleared but the local profile
  // still exists (e.g. mid-flight offline session).
  const [localUserName, setLocalUserName] = useState<string | null>(() => {
    return localStorage.getItem('lastFullName') || null
  })

  useEffect(() => {
    if (localUserName) return
    db.userProfiles
      .orderBy('lastModified')
      .reverse()
      .toArray()
      .then((profiles) => {
        const named = profiles.find((p) => p.fullName && p.fullName.trim().length > 0)
        if (named?.fullName) setLocalUserName(named.fullName)
      })
      .catch((err) => console.error('[SetupPage] Failed to load local user name:', err))
  }, [localUserName])

  // Window resize on mount - Only resize if user hasn't completed onboarding yet
  // Once user is on tasks, they can freely resize and we respect their choice
  useEffect(() => {
    // Check if user has already been to TaskListPage (setup completed)
    const setupCompleted = localStorage.getItem('onboarding_completed') === 'true'

    if (!setupCompleted && platformAPI.api?.window?.resize) {
      platformAPI.api.window.resize(860, 500, true).then((success) => {
        if (success && platformAPI.api?.invoke) {
          platformAPI.api.invoke('save-window-size')
        }
      })
    }
  }, [platformAPI.api])

  /**
   * View State: login, register, forgotPassword, resetPassword
   * Smart Default: Shows login mode if user has logged in before
   */
  type AuthView = 'login' | 'register' | 'forgotPassword' | 'resetPassword'
  const [authView, setAuthView] = useState<AuthView>(() => {
    // Check if we're in password recovery mode
    // Set by:
    //  - AuthProvider (PASSWORD_RECOVERY event from Supabase — PKCE flow)
    //  - main.ts / index.html inline script (legacy hash-based flow)
    const isRecoveryMode = localStorage.getItem('passwordRecoveryMode') === 'true'

    // Electron legacy: Hash is set by main.ts, stored in localStorage AND URL hash
    const hasRecoveryHashElectron = localStorage.getItem('passwordRecoveryHash') !== null
    const hasRecoveryTokenInURL = window.location.hash.includes('type=recovery')

    // Web (PKCE): No hash anymore — flag alone is authoritative, because it's
    // only set by the PASSWORD_RECOVERY event which Supabase emits exclusively
    // after a successful recovery-token exchange.
    // Electron legacy: still requires a token source for safety (prevents stale flags).
    const isValidRecovery = platformAPI.platform === 'web'
      ? isRecoveryMode
      : (isRecoveryMode && (hasRecoveryHashElectron || hasRecoveryTokenInURL))

    // Only show resetPassword if BOTH flag is set AND recovery token exists
    // This prevents showing reset screen on fresh app start when token is expired
    if (isValidRecovery) {
      return 'resetPassword'
    }

    // Clear stale flag if recovery is invalid (no token/hash found)
    if (isRecoveryMode && !isValidRecovery) {
      localStorage.removeItem('passwordRecoveryMode')
      localStorage.removeItem('passwordRecoveryHash') // Clean up hash too
    }

    // Smart Default: Shows login mode if user has logged in before
    const hasLoggedInBefore = localStorage.getItem('hasLoggedInBefore')
    const defaultView = hasLoggedInBefore === 'true' ? 'login' : 'register'
    return defaultView
  })

  // Automatic redirect to Electron app when on web and in resetPassword mode
  // ONLY if user came from Electron app (source=electron parameter in URL)
  useEffect(() => {
    if (authView === 'resetPassword' && platformAPI.platform === 'web') {
      // Check URL parameter to determine if this came from Electron or Web
      const urlParams = new URLSearchParams(window.location.search)
      const source = urlParams.get('source')

      // Only trigger deep link if source=electron (user clicked "Passwort vergessen" in Electron app)
      if (source !== 'electron') {
        // User came from web browser "Passwort vergessen" → Stay in browser
        return
      }

      // User came from Electron app → Trigger deep link to open Electron app
      const recoveryHash = sessionStorage.getItem('passwordRecoveryHash') || ''

      // Convert hash to query parameters (Windows strips hash from protocol URLs)
      let deepLink = 'happytask://password-reset/'
      if (recoveryHash) {
        // Remove leading # and convert to query params
        const hashWithoutSymbol = recoveryHash.startsWith('#') ? recoveryHash.substring(1) : recoveryHash
        deepLink = `happytask://password-reset/?${hashWithoutSymbol}`

        // Clean up sessionStorage
        sessionStorage.removeItem('passwordRecoveryHash')
      }

      // Small delay to allow RedirectingToApp component to render first
      const redirectTimer = setTimeout(() => {
        window.location.href = deepLink
      }, 500)
      return () => clearTimeout(redirectTimer)
    }
  }, [authView, platformAPI.platform])

  // Backwards compatibility: isLoginMode computed from authView
  const isLoginMode = authView === 'login'

  // Email & OTP States (for Registration)
  const [email, setEmail] = useState('')
  const [emailError, setEmailError] = useState(false)
  const [emailErrorMessage, setEmailErrorMessage] = useState('')
  const [showCodeInput, setShowCodeInput] = useState(false)
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [canResendCode, setCanResendCode] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(60)

  // Password State (for Login)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState(false)

  // Refs for inputs to set focus on errors
  const emailInputRef = useRef<HTMLInputElement>(null)
  const passwordInputRef = useRef<HTMLInputElement>(null)
  const codeInputRef = useRef<HTMLInputElement>(null)

  // Platform-spezifische CSS-Klasse
  const platformClass = platformAPI.platform === 'electron' ? 'setup-page--electron' : 'setup-page--web'

  // Set focus on email input when page loads or mode changes
  useEffect(() => {
    if (emailInputRef.current && !showCodeInput) {
      emailInputRef.current.focus()
    }
  }, [showCodeInput, isLoginMode])

  // Event Listener für Skip-Button aus Titlebar
  useEffect(() => {
    const handleSkipClick = () => {
      setShowSkipConfirmation(true)
    }

    // Custom Event für Skip-Button (wird von main.tsx SkipButton ausgelöst)
    window.addEventListener('setup-skip-clicked', handleSkipClick)

    return () => {
      window.removeEventListener('setup-skip-clicked', handleSkipClick)
    }
  }, [])

  // Event Listener für PASSWORD_RECOVERY (Supabase Auth State Change)
  useEffect(() => {
    if (!supabase) return

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // User clicked reset link - show reset password view
        setAuthView('resetPassword')
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [])

  // Event dispatchen wenn Dialog angezeigt/versteckt wird
  useEffect(() => {
    if (showSkipConfirmation) {
      window.dispatchEvent(new Event('setup-skip-dialog-show'))
    } else {
      window.dispatchEvent(new Event('setup-skip-dialog-hide'))
    }
  }, [showSkipConfirmation])

  // Event dispatchen wenn authView sich ändert
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setup-auth-view-changed', { detail: authView }))
  }, [authView])

  // Countdown-Timer für OTP Resend
  useEffect(() => {
    if (!showCodeInput) return

    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          setCanResendCode(true)
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [showCodeInput])

  /**
   * Validates email format using regex
   * @param email - Email address to validate
   * @returns true if email is valid, false otherwise
   */
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  /**
   * Handles skip button click
   * Sets storage mode to offline (Electron only) and navigates to profile setup
   */
  const handleSkip = async () => {
    // Close dialog first
    setShowSkipConfirmation(false)

    // Nur für Electron: Setze Storage Mode auf offline
    if (platformAPI.platform === 'electron') {
      await platformAPI.setStorageMode('offline')
    }
    // Set loginMethod to 'offline' to indicate offline mode
    localStorage.setItem('loginMethod', 'offline')
    // Navigate to profile setup (offline mode requires only name, no password)
    navigate('/setup-profile')
  }

  /**
   * Toggles between login and registration mode
   * Resets all form states when switching modes
   */
  const handleLoginModeToggle = () => {
    // Toggle zwischen Login und Registrierung
    setAuthView(authView === 'login' ? 'register' : 'login')
    // Reset all form states
    setEmail('')
    setPassword('')
    setCode('')
    setEmailError(false)
    setPasswordError(false)
    setCodeError(false)
    setShowCodeInput(false)
  }

  /**
   * Handles email + password login flow
   * Validates credentials, authenticates user, and navigates to home
   */
  const handlePasswordLogin = async () => {
    // Validierung
    if (!isValidEmail(email)) {
      setEmailError(true)
      emailInputRef.current?.focus()
      return
    }

    if (!password || password.length < 6) {
      setPasswordError(true)
      passwordInputRef.current?.focus()
      return
    }

    setEmailError(false)
    setPasswordError(false)
    setIsSubmitting(true)

    try {
      // Login mit Email + Passwort
      const result = await loginUser({ email, password })

      if (result.success && result.user && result.session) {
        // Erfolg: Cloud-Sync Mode setzen (nur Electron)
        if (platformAPI.platform === 'electron') {
          await platformAPI.setStorageMode('cloud-sync')
        }
        // Merke: User hat sich erfolgreich eingeloggt (für Smart Default)
        localStorage.setItem('hasLoggedInBefore', 'true')
        // Merke: Login mit Passwort → direkt zu Home
        localStorage.setItem('loginMethod', 'password')
        // Setze profileSetupCompleted Flag (Login User hat bereits ein vollständiges Profil)
        localStorage.setItem('profileSetupCompleted', 'true')
        // Start setup flow tracking (for returning users)
        await startSetupFlow()

        // WICHTIG: Prüfe ob User Setup-Fortschritt hat (könnte beim Onboarding abgebrochen haben)
        const progress = await getSetupFlowProgress()

        if (progress && !progress.completed && progress.step !== 'login' && progress.step !== 'profile') {
          // User hat Setup-Fortschritt (aber nicht login/profile) → zur letzten Seite
          const targetRoute = getRouteForStep(progress.step)
          navigate(targetRoute)
        } else {
          // Server says setup is complete — rehydrate localStorage flag that
          // ProtectedRoute relies on (wiped by wipeUserLocalState on logout).
          if (progress?.completed) {
            localStorage.setItem('onboarding_completed', 'true')
          }
          navigate('/home')
        }
      } else {
        // Fehler: Ungültige Credentials
        setPasswordError(true)
        passwordInputRef.current?.focus()
      }
    } catch (error) {
      setPasswordError(true)
      passwordInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleContinue = async () => {
    // Login-Modus: Email + Passwort Login
    if (isLoginMode) {
      await handlePasswordLogin()
      return
    }

    // Registrierungs-Modus: Wenn Code-Input bereits sichtbar ist → Code verifizieren
    if (showCodeInput) {
      await handleVerifyCode()
      return
    }

    // Email validieren (für OTP-Versand)
    if (!isValidEmail(email)) {
      setEmailError(true)
      emailInputRef.current?.focus()
      return
    }

    setEmailError(false)
    setIsSubmitting(true)

    try {
      // OTP-Code per Email versenden
      const result = await sendEmailOTP(email, language)

      if (result.success) {
        // Code-Input anzeigen und Countdown starten
        setShowCodeInput(true)
        setCanResendCode(false)
        setResendCountdown(60)
        setEmailError(false)
        setEmailErrorMessage('')
      } else {
        // Fehler anzeigen - prüfe bekannte Supabase-Fehler
        const errorMsg = result.error || ''
        if (errorMsg.includes('you can only request this after')) {
          // Client-seitiges Cooldown zwischen gleichen Anfragen
          setEmailError(false)
          setEmailErrorMessage(t('setup.otpRateLimitError'))
        } else if (errorMsg.includes('email rate limit exceeded')) {
          // Supabase over_email_send_rate_limit — server-seitiges E-Mail-Limit
          setEmailError(false)
          setEmailErrorMessage(t('setup.otpEmailRateLimitError'))
        } else {
          // Anderer Fehler - markiere E-Mail-Feld rot
          setEmailError(true)
          setEmailErrorMessage('')
          emailInputRef.current?.focus()
        }
      }
    } catch (error) {
      setEmailError(true)
      emailInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleVerifyCode = async () => {
    if (!code || code.length < 6) {
      setCodeError(true)
      codeInputRef.current?.focus()
      return
    }

    setCodeError(false)
    setIsSubmitting(true)

    try {
      // Code verifizieren
      const result = await verifyEmailOTP(email, code)

      if (result.success && result.user && result.session) {
        // Erfolg: OTP verifiziert

        // Sprache in user_metadata absichern (Edge Case: User hat Sprache zwischen
        // Code-Versand und Code-Eingabe gewechselt)
        await syncUserLanguage(language)

        // Merke: User hat sich erfolgreich eingeloggt (für Smart Default)
        localStorage.setItem('hasLoggedInBefore', 'true')

        // Set loginMethod flag FIRST before any navigation or session refresh
        localStorage.setItem('loginMethod', 'otp')

        // Session neu laden, damit user_metadata aktuell ist
        let refreshedUser = result.user
        if (supabase) {
          const { data } = await supabase.auth.refreshSession()
          refreshedUser = data.user || result.user
        }

        // Cloud-Sync Mode setzen (nur Electron)
        if (platformAPI.platform === 'electron') {
          await platformAPI.setStorageMode('cloud-sync')
        }

        // Start setup flow tracking (for new users)
        // This resets setup_flow_step to 'login' for fresh start
        await startSetupFlow()

        // IMPORTANT: Do NOT navigate here!
        // Let SetupFlowAutoResume handle ALL navigation
      } else {
        // Fehler: Ungültiger Code
        setCodeError(true)
        codeInputRef.current?.focus()
      }
    } catch (error) {
      setCodeError(true)
      codeInputRef.current?.focus()
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleResendCode = async () => {
    if (!canResendCode) return

    setIsSubmitting(true)
    setCodeError(false)

    try {
      const result = await sendEmailOTP(email, language)

      if (result.success) {
        // Timer neu starten
        setCanResendCode(false)
        setResendCountdown(60)
      }
    } catch (error) {
      // Silent error handling
    } finally {
      setIsSubmitting(false)
    }
  }

  /**
   * Handles Google OAuth flow by opening system browser to oauth.happytask.app
   * Works for both Electron and Web platforms
   */
  const handleGoogleLogin = () => {
    const platform = platformAPI.platform === 'electron' ? 'electron' : 'web'
    const authUrl = `https://oauth.happytask.app/google?platform=${platform}`

    if (platformAPI.platform === 'electron' && window.api) {
      // Electron: Use shell.openExternal
      window.api.openExternal(authUrl)
    } else {
      // Web: Use window.open
      window.open(authUrl, '_blank')
    }
  }

  // Special case: Show full-screen RedirectingToApp on web for password reset from Electron
  if (authView === 'resetPassword' && platformAPI.platform === 'web') {
    const urlParams = new URLSearchParams(window.location.search)
    const source = urlParams.get('source')

    if (source === 'electron') {
      return <RedirectingToApp />
    }
  }

  return (
    <div className={`setup-page ${platformClass}`}>
      {/* Open in App Prompt (Web only, for password reset) */}
      {showOpenInAppPrompt && (
        <OpenInAppPrompt onDismiss={() => setShowOpenInAppPrompt(false)} />
      )}

      {/* Scrollable Container */}
      <div className="setup-page__scrollable-container">
        <div className="setup-page__scrollable-inner">

          {/* Content Area */}
          <div className="setup-page__content-area">
            {/* Left Side - Welcome (hidden for forgotPassword and resetPassword) */}
            {authView !== 'forgotPassword' && authView !== 'resetPassword' && (
              <div className="setup-page__welcome-section">
                <div className="setup-page__welcome-content">
                  <SmileyIcon size={64} />
                  <h1 className="setup-page__welcome-title">
                    {isLoginMode
                      ? (localUserName
                        ? t('setup.welcomeBackName', { name: localUserName })
                        : t('setup.welcomeBack'))
                      : t('setup.welcome')}
                  </h1>
                  {!isLoginMode && (
                    <p className="setup-page__welcome-subtitle">
                      {t('setup.subtitleRegister')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Right Side - Form */}
            <div className="setup-page__form-section">
              {/* Forgot Password View */}
              {authView === 'forgotPassword' && (
                <ForgotPasswordForm onBackToLogin={() => setAuthView('login')} />
              )}

              {/* Reset Password View (Both Web and Electron) */}
              {authView === 'resetPassword' && (
                <ResetPasswordForm onBackToLogin={() => {
                  // Clear password recovery flags when user clicks "Back to Login"
                  localStorage.removeItem('passwordRecoveryMode')
                  localStorage.removeItem('passwordRecoveryHash')
                  sessionStorage.removeItem('passwordRecoveryHash')
                  setAuthView('login')
                }} />
              )}

              {/* Login / Register View */}
              {(authView === 'login' || authView === 'register') && (
                <>
              {/* Social Login Buttons */}
              <div className="setup-page__social-buttons">
                <SocialButton provider="google" onClick={handleGoogleLogin}>{t('setup.continueWithGoogle')}</SocialButton>
                <SocialButton provider="apple">{t('setup.continueWithApple')}</SocialButton>
              </div>

              {/* Separator */}
              <div className="setup-page__separator">
                <div className="setup-page__separator-line"></div>
                <span className="setup-page__separator-text">
                  {t('setup.or')}
                </span>
                <div className="setup-page__separator-line"></div>
              </div>

              {/* Email & Password Container */}
              <div className="setup-page__credentials-container">
                {/* Email Input */}
                <Input
                  ref={emailInputRef}
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value)
                    setEmailError(false)
                    setPasswordError(false) // Auch Password-Error zurücksetzen
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleContinue()
                    }
                  }}
                  placeholder={t('setup.emailPlaceholder')}
                  disabled={showCodeInput && !isLoginMode}
                  error={emailError}
                />
                {emailError && (
                  <p className="setup-page__error-message">{t('setup.emailInvalid')}</p>
                )}
                {emailErrorMessage && (
                  <p className="setup-page__error-message">{emailErrorMessage}</p>
                )}

                {/* Password Input (nur im Login-Modus) */}
                {isLoginMode && (
                  <>
                    <PasswordInput
                      ref={passwordInputRef}
                      name="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => {
                        setPassword(e.target.value)
                        setPasswordError(false)
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          handleContinue()
                        }
                      }}
                      placeholder={t('setup.passwordPlaceholder')}
                      error={passwordError}
                    />
                    {passwordError && (
                      <p className="setup-page__error-message">{t('setup.passwordInvalid')}</p>
                    )}
                    <div className="setup-page__password-forgot">
                      <Link onClick={() => setAuthView('forgotPassword')}>{t('setup.passwordForgot')}</Link>
                    </div>
                  </>
                )}
              </div>

              {/* Code Input (nur sichtbar nach Email-Versand im Registrierungs-Modus) */}
              {!isLoginMode && showCodeInput && (
                <div className="setup-page__code-section">

                  <Input
                    ref={codeInputRef}
                    type="text"
                    value={code}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '') // Nur Zahlen
                      if (value.length <= 8) {
                        setCode(value)
                        setCodeError(false)
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleContinue()
                      }
                    }}
                    maxLength={8}
                    placeholder={t('setup.codePlaceholder')}
                    autoFocus
                    error={codeError}
                    className="setup-page__code-input"
                  />
                  {codeError && (
                    <p className="setup-page__error-message">{t('setup.codeInvalid')}</p>
                  )}
                  <p className="setup-page__code-sent-message">{t('setup.codeSentMessage')}</p>
                  <button
                    onClick={handleResendCode}
                    disabled={!canResendCode || isSubmitting}
                    className="setup-page__resend-button"
                    type="button"
                  >
                    {canResendCode
                      ? t('setup.resendCode')
                      : t('setup.resendCountdown', { seconds: resendCountdown })}
                  </button>
                </div>
              )}

              {/* Terms Text (nur im Registrierungs-Modus) */}
              {!isLoginMode && (
                <p className="setup-page__terms-text">
                  <span>{t('setup.termsPrefix')} </span>
                  <Link href="https://happytask.app/">{t('setup.termsLink')}</Link>
                  <span> {t('setup.termsMiddle')} </span>
                  <Link href="https://happytask.app/">{t('setup.privacyLink')}</Link>
                  <span>{t('setup.termsSuffix')}</span>
                </p>
              )}
                </>
              )}
            </div>
          </div>

          {/* Bottom Action Bar - Only for login/register views */}
          {(authView === 'login' || authView === 'register') && (
            <div className="setup-page__action-bar">
              <div className="setup-page__action-bar-content">
                <div className="setup-page__sign-in-section">
                  <span className="setup-page__sign-in-text">
                    {isLoginMode ? t('setup.noAccount') : t('setup.hasAccount')}
                  </span>
                  <Link onClick={handleLoginModeToggle}>
                    {isLoginMode ? t('setup.register') : t('setup.signIn')}
                  </Link>
                </div>
                <Button
                  onClick={handleContinue}
                  shortcut={{ variant: 'return' }}
                  disabled={isSubmitting}
                >
                  {isSubmitting
                    ? isLoginMode
                      ? t('setup.loggingIn')
                      : showCodeInput
                      ? t('setup.verifying')
                      : t('setup.sendingCode')
                    : isLoginMode
                    ? t('setup.login')
                    : t('setup.continue')}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Skip Confirmation Dialog - Nur für Electron */}
      {platformAPI.platform === 'electron' && (
        <SkipConfirmationDialog
          isOpen={showSkipConfirmation}
          onClose={() => setShowSkipConfirmation(false)}
          onConfirm={handleSkip}
          platform={windowContext.window?.platform}
        />
      )}
    </div>
  )
}
